import type { Evaluation, MarketDef, PlanDef, RuleSet, PayoutPolicy, AccountSnapshot } from '@ck/core';

export type { Evaluation, MarketDef, PlanDef, RuleSet, PayoutPolicy, AccountSnapshot };

export interface PublicPlan {
  id: string; brand: PlanDef['brand']; name: string; tag: string; description: string;
  rewardSplitPct: number; leverage: number; payout: PayoutPolicy; final: boolean; version: number;
  sizes: { size: number; priceUsd: number }[];
  phases: { key: string; name: string; funded: boolean; rules: RuleSet }[];
}

export interface LiveAccountType { id: string; name: string; tag: string; minDepositUsd: number; spreadFrom: string; commission: string; leverage: string }
export interface Prediction { id: string; question: string; category: string; yesCents: number; closes: string }

export interface MarketWorkspace {
  market: MarketDef;
  plans: PublicPlan[];
  liveAccountTypes: LiveAccountType[];
  predictions: Prediction[];
}

export interface ChallengeAccount {
  id: string; traderId: string; brand: PlanDef['brand']; planId: string; plan: PlanDef; size: number;
  phaseIndex: number; status: 'active' | 'failed' | 'funded' | 'funding_review';
  connectorId: string; externalId: string; login: string; server: string; createdAt: string;
  history: { phaseKey: string; login: string; result: 'passed' | 'failed' | 'reinstated' | 'reset' | 'moved'; at: string; detail?: string }[];
  splitPct?: number; splitOverride?: boolean;
}

export interface AccountView { account: ChallengeAccount; evaluation: Evaluation | null; snapshot: AccountSnapshot | null }

export interface TenantBranding { name: string; mark: string; logoUrl: string | null; primary: string; primaryInk: string; paper: string; ink: string; supportEmail: string; loginVideoUrl?: string | null }
export interface PaymentMethod { id: string; label: string; instructions: string; enabled: boolean; kind?: 'mobile_money' | 'bank' | 'card' | 'crypto' | 'local' | 'other'; countries?: string[]; priority?: number; backupId?: string | null; monthlyCapUsd?: number | null; status?: 'up' | 'down'; network?: string | null; address?: string | null }
export interface CertificateTemplate { heading: string; signatory: string; signatoryTitle: string; footer: string }
export interface PublicTenant { id: string; legalName: string; branding: TenantBranding; markets: MarketDef[]; paymentMethods: PaymentMethod[]; maintenance?: { on: boolean; message: string } | null; certificate?: CertificateTemplate }

export interface Trader { id: string; email: string; firstName: string; lastName: string; phone: string | null; country: string; kycStatus: string; status?: string; totpEnabled?: boolean; createdAt: string; avatarUrl?: string | null; access?: { hiddenMarkets: string[]; hiddenFeatures: string[] } }
export interface Order {
  id: string; planId: string; planName?: string; brand?: string; planVersion: number; size: number; amountUsd: number; method: string;
  couponCode: string | null; discountUsd: number; reference: string | null; status: string; accountId: string | null; note: string | null; createdAt: string; paidAt: string | null; refundStatus?: string | null; refundedAt?: string | null }
export interface Payout { id: string; kind: string; accountId: string | null; amountUsd: number; method: string; destination: string; status: string; note: string | null; createdAt: string; reviewedAt: string | null }

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly body: Record<string, unknown> = {}) { super(message); }
  get issues() { return (this.body.issues as { path: (string | number)[]; message: string }[] | undefined) ?? []; }
}

export interface ClientOptions {
  baseUrl: string;
  /** Force a tenant (local development). In production the browser origin decides. */
  tenant?: string;
  /** Returns the current session token, if signed in. */
  token?: () => string | null;
  /** Called when the API says the session is no longer valid. */
  onUnauthorized?: () => void;
  /** A stable id for this browser, used by fraud checks. */
  device?: () => string | null;
}

/** Typed client for the CK API, shared by the client portal, affiliate portal and CRM. */
export function createClient(opts: ClientOptions) {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
    if (body !== undefined && !isForm) headers['content-type'] = 'application/json';
    const token = opts.token?.();
    if (token) headers.authorization = `Bearer ${token}`;
    if (opts.tenant) headers['x-ck-tenant'] = opts.tenant;
    const dev = opts.device?.(); if (dev) headers['x-ck-device'] = dev;
    let res: Response;
    try {
      res = await fetch(opts.baseUrl.replace(/\/$/, '') + path, { method, headers, body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body) });
    } catch {
      throw new ApiError('We can’t reach the server. Check your connection and try again.', 0);
    }
    const data = await res.json().catch(() => ({}));
    if (res.status === 401 && token && !(data as { needsCode?: boolean }).needsCode) opts.onUnauthorized?.();
    if (!res.ok) throw new ApiError((data as { error?: string }).error ?? `Request failed (${res.status})`, res.status, data as Record<string, unknown>);
    return data as T;
  }
  /** Downloads a protected file (e.g. a KYC document) as an object URL. */
  async function blobUrl(path: string): Promise<string> {
    const headers: Record<string, string> = {};
    const token = opts.token?.();
    if (token) headers.authorization = `Bearer ${token}`;
    if (opts.tenant) headers['x-ck-tenant'] = opts.tenant;
    const res = await fetch(opts.baseUrl.replace(/\/$/, '') + path, { headers });
    if (!res.ok) throw new ApiError('Could not load the file.', res.status);
    return URL.createObjectURL(await res.blob());
  }
  return {
    get: <T>(path: string) => call<T>('GET', path),
    post: <T>(path: string, body: unknown = {}) => call<T>('POST', path, body),
    put: <T>(path: string, body: unknown) => call<T>('PUT', path, body),
    patch: <T>(path: string, body: unknown) => call<T>('PATCH', path, body),
    del: <T>(path: string) => call<T>('DELETE', path),
    blobUrl,
    tenant: () => call<PublicTenant>('GET', '/v1/tenant'),
    workspace: (brand: string) => call<MarketWorkspace>('GET', `/v1/markets/${brand}`),
  };
}

export type CkClient = ReturnType<typeof createClient>;

/** Session token storage that never throws (private mode, blocked storage). */
export function tokenStore(key: string) {
  let memory: string | null = null;
  return {
    get(): string | null { try { return localStorage.getItem(key) ?? memory; } catch { return memory; } },
    set(v: string | null) { memory = v; try { if (v) localStorage.setItem(key, v); else localStorage.removeItem(key); } catch { /* memory only */ } },
  };
}

export const usd = (n: number, max = 2) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: Number.isInteger(n) ? 0 : Math.min(2, max), maximumFractionDigits: max });
export const sizeLabel = (n: number) => (n >= 1000 ? `$${n / 1000}K` : `$${n}`);
export const dateLabel = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
export const dateTimeLabel = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

/** Apply a tenant's colours so every firm's portal looks like its own brand. */
export function applyBranding(b: TenantBranding, root: HTMLElement = document.documentElement) {
  // Only the accent follows the firm's brand; surfaces stay neutral so every firm's numbers read cleanly.
  root.style.setProperty('--brand', b.primary);
  root.style.setProperty('--brand-ink', b.primaryInk);
  root.style.setProperty('--amber', b.primary);
}

/**
 * Where the API lives. Our own domains use api.ckfundedpro.com (or VITE_API_URL);
 * the temporary *.vercel.app addresses talk to the Render service directly until DNS is set up.
 */
export function apiBase(configured: string | undefined): string {
  if (typeof location !== 'undefined' && location.hostname.endsWith('.vercel.app')) return 'https://ck-api-xmpo.onrender.com';
  return configured ?? 'http://localhost:4000';
}

/** A per-browser id plus a light fingerprint of the device (screen, time zone, language, CPU). Not personal data on its own. */
export function deviceId(): string {
  const key = 'ck.device';
  let id: string | null = null;
  try { id = localStorage.getItem(key); } catch { /* private mode */ }
  if (!id) { id = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)).replace(/-/g, '').slice(0, 24); try { localStorage.setItem(key, id); } catch { /* ignore */ } }
  const n = navigator as Navigator & { deviceMemory?: number };
  const raw = [screen.width, screen.height, screen.colorDepth, Intl.DateTimeFormat().resolvedOptions().timeZone, navigator.language, navigator.platform, navigator.hardwareConcurrency, n.deviceMemory ?? ''].join('|');
  let h = 2166136261; for (let i = 0; i < raw.length; i++) { h ^= raw.charCodeAt(i); h = Math.imul(h, 16777619); }
  return `${id}.${(h >>> 0).toString(36)}`;
}

/** WebSocket address of the support-call audio relay. */
export function callRelayUrl(baseUrl: string, callId: string, side: 'client' | 'staff', token: string | null) {
  return `${baseUrl.replace(/\/$/, '').replace(/^http/, 'ws')}/v1/calls/${callId}/relay?side=${side}&t=${encodeURIComponent(token ?? '')}`;
}
